import { ApiError, type ApiClient } from "@hakili/api-client";
import {
  declaredMonthsSchema,
  depositLinkConfirmBodySchema,
  documentConfirmBodySchema,
  type DepositLinkConfirmBody,
  type DocumentConfirmBody,
} from "@hakili/contracts";
import { t } from "@hakili/i18n";

/**
 * `round C7`, `C7.1` and `C7.3`: **the buyer's pièces, by the doors that own
 * them.**
 *
 * Every function takes the `ApiClient` rather than closing over the session,
 * as `launchVerificationBy` does: a screen's unit spec mounts the control over
 * one of these and a recording fetch, and the request's method and path are
 * the witness that the control calls a door , not the control's presence.
 *
 * **The upload is three crossings, each an HTTP call by the browser**: the
 * ticket from Hakili's door, the browser's own `PUT` to the presigned URL the
 * ticket names, then the confirmation to Hakili's door. The presigned URL is a
 * credential: it is used and never kept, never logged, never shown.
 */

export interface ClientDocumentView {
  readonly code: string;
  readonly label: string;
  /** `OD-37` R7: the day the founder retired this piece (`YYYY-MM-DD`), else `null`. */
  readonly retiredOn?: string | null;
  /** `attendue` (missing or rejected), `recue`. */
  readonly status: "attendue" | "recue";
  readonly helpText: string | null;
  /** The chargé's reason when the last deposit was rejected. */
  readonly rejectionReason: string | null;
  readonly receivedAt: string | null;
  /**
   * `S4-5-MULTI-FILE`: true for the two pièces whose own label asks for several documents , the
   * row offers « Ajouter un autre fichier » once received instead of closing to one file.
   */
  readonly multiFile: boolean;
  /**
   * `CANON-1`: how many documents this pièce asks for, `null` unless it is `multi_file`. Null is what
   * keeps the count line off every pièce that asks for one, rather than a comparison in the screen.
   */
  readonly expectedFiles: number | null;
  /** `CANON-1`: how many files it actually holds. */
  readonly receivedFiles: number;
  /**
   * `PIECES-COUNT-ENFORCE` (`P0-DOS-064`): the months the pièce covers, the sum of the months its client declared, served beside `receivedFiles`; `null` for a pièce counted in files.
   * Its presence is the door's word that the count line is in months, the screen decides nothing.
   */
  readonly coveredMonths?: number | null;
  /**
   * `CLIENT-ONE-TRUTH` (A-H, 2 November): once a reading of Hakili speaks for the pièce, its sentence (« Les bulletins de salaire envoyés ne couvrent pas août 2026 : ajoutez le document de ce mois. »,
   * or « Mois lus par Hakili : … ») is served and `expectedFiles` / `coveredMonths` are `null`; `null` before a reading. The screen draws it verbatim and composes nothing.
   */
  readonly monthsSentence?: string | null;
  readonly readMonths?: readonly string[] | null;
  readonly missingMonths?: readonly string[] | null;
}

export interface VehicleDocumentView {
  readonly code: "carteGrise" | "factureProforma" | "visiteTechnique" | "expertise";
  readonly received: boolean;
  /** `VISITE-SANS-OBJET` (17 October 2026): the chargé marked it not applicable , neither received nor awaited. */
  readonly notApplicable?: boolean;
}

export interface ClientDocuments {
  readonly pieces: readonly ClientDocumentView[];
  /** DERIVED by the door on every read (`P1-DOS-013`), never counted here. */
  readonly manquants: number;
  readonly vehicleDocuments: readonly VehicleDocumentView[];
}

interface UploadTicket {
  readonly id: string;
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
}

export interface DepositLinkView {
  readonly label: string;
  readonly pieceCode: string;
  readonly state: "ouvert" | "utilise" | "expire" | "revoque";
  readonly expiresAt: string;
  /** `S4-6`: who owes the pièce , the page's few supplier-facing sentences read this. */
  readonly supplier: "client" | "vendeur";
  /** `DEPOSIT-LINK-MONTHS`: true for a pièce that asks for several documents; the page then asks how many months a file contains. */
  readonly multiFile: boolean;
  /** `DEPOSIT-LINK-MONTHS`: how many months a `multiFile` pièce asks for; `null` for a pièce that asks for one document. */
  readonly expectedFiles: number | null;
}

const documentsFrom = (answer: {
  pieces?: unknown;
  manquants?: unknown;
  vehicleDocuments?: unknown;
}): ClientDocuments => ({
  pieces: Array.isArray(answer.pieces) ? (answer.pieces as ClientDocumentView[]) : [],
  manquants: typeof answer.manquants === "number" ? answer.manquants : 0,
  vehicleDocuments: Array.isArray(answer.vehicleDocuments)
    ? (answer.vehicleDocuments as VehicleDocumentView[])
    : [],
});

/** `GET /me/dossiers/{id}/pieces`: the list the buyer owes, and the vehicle's beside it. */
export const readClientDocuments = async (api: ApiClient, dossierId: string): Promise<ClientDocuments> =>
  documentsFrom(
    await api.request<{ pieces?: unknown; manquants?: unknown; vehicleDocuments?: unknown }>(
      "/api/v1/me/dossiers/{id}/pieces",
      "get",
      { params: { id: dossierId } },
    ),
  );

/**
 * The browser's own crossing: the file goes to the URL the ticket names, with
 * the headers the ticket names, and nothing else is added. A refusal from the
 * store is a refusal , the confirmation is never asked for a PUT that failed.
 */
const putTo = async (fetchImpl: typeof globalThis.fetch, ticket: UploadTicket, file: File): Promise<void> => {
  const response = await fetchImpl(ticket.url, {
    method: "PUT",
    headers: { ...ticket.headers },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`${t("client.dossier.depotRefuse")} (${String(response.status)})`);
  }
};

/**
 * **A deposit that fails says why, in words , never nothing, never the browser's English.**
 *
 * Three silences were possible and each reached the row as a blank or foreign line:
 * - the browser's `fetch` rejects with `TypeError: Failed to fetch` when the store's answer carries no
 *   CORS header (a refused PUT, a missing rule, a dropped connection) , the page cannot tell which, and
 *   the raw English was what the row printed;
 * - an `ApiError` whose body held no sentence (a gateway's HTML page, a 5xx) has the message `""`, and
 *   a row that prints `""` prints an empty alert;
 * - any other `Error` with no message.
 * The door's own French sentence, when it sent one, is carried through untouched.
 */
export const depositFailureWords = (failure: unknown): string => {
  if (failure instanceof ApiError) {
    return failure.message === "" ? t("client.dossier.depotSansMotif") : failure.message;
  }
  if (failure instanceof TypeError) return t("client.dossier.depotInjoignable");
  if (failure instanceof Error && failure.message !== "") return failure.message;
  return t("common.error.reseau");
};

const confirmBodyOf = (ticketId: string, declaredMonths: number | undefined): DocumentConfirmBody =>
  documentConfirmBodySchema.parse(declaredMonths === undefined ? { ticketId } : { ticketId, declaredMonths });

/** `DEPOSIT-LINK-MONTHS`: the confirm's body, by the contract's strict schema; `declaredMonths` rides only when the page asked it. */
const linkConfirmBodyOf = (ticketId: string, declaredMonths: number | undefined): DepositLinkConfirmBody =>
  depositLinkConfirmBodySchema.parse(
    declaredMonths === undefined ? { ticketId } : { ticketId, declaredMonths },
  );

const ticketOf = (answer: { ticket?: unknown }): UploadTicket => {
  const ticket = answer.ticket as Partial<UploadTicket> | undefined;
  if (ticket === undefined || typeof ticket.id !== "string" || typeof ticket.url !== "string") {
    throw new Error(t("client.session.inattendue"));
  }
  return { id: ticket.id, url: ticket.url, headers: ticket.headers ?? {} };
};

/**
 * WF-CLI-18 step 6, « Envoyer » on ONE named pièce: ticket, PUT, confirm.
 * Answers the list as the door re-reads it after the confirmation.
 */
export const depositClientDocument = async (
  api: ApiClient,
  fetchImpl: typeof globalThis.fetch,
  dossierId: string,
  code: string,
  file: File,
  declaredMonths?: number,
): Promise<ClientDocuments> => {
  // `PIECES-COUNT` (A48): the confirmation's body is `@hakili/contracts`' `documentConfirmBodySchema`, the doors' own. A count outside it is refused
  // here, before a ticket is asked or a byte leaves, never after an upload the door would then refuse.
  declaredMonthsSchema.optional().parse(declaredMonths);
  const ticket = ticketOf(
    await api.request<{ ticket?: unknown }>("/api/v1/me/dossiers/{id}/pieces/{piece}/upload-ticket", "post", {
      params: { id: dossierId, piece: code },
      body: { fileName: file.name, mimeType: file.type, sizeBytes: file.size },
    }),
  );
  await putTo(fetchImpl, ticket, file);
  const confirmed = await api.request<{ pieces?: unknown; manquants?: unknown }>(
    "/api/v1/me/dossiers/{id}/pieces/{piece}/confirm",
    "post",
    {
      params: { id: dossierId, piece: code },
      // `PIECES-COUNT`: the months this file covers, said by the buyer for a `multi_file` pièce alone; absent, the door reads one.
      body: confirmBodyOf(ticket.id, declaredMonths),
    },
  );
  // The confirmation answers the buyer's list alone; the vehicle's pièces are
  // re-read by the caller, they did not move by his deposit.
  return documentsFrom(confirmed);
};

/** WF-CLI-18 step 7: « Envoyer mon dossier à la vérification », the buyer's own hand. */
export const submitClientDocuments = async (api: ApiClient, dossierId: string): Promise<unknown> => {
  const answer = await api.request<{ dossier?: unknown }>("/api/v1/me/dossiers/{id}/submit-pieces", "post", {
    params: { id: dossierId },
  });
  return answer.dossier ?? null;
};

/**
 * `C7.3`, the public page: the link's token IS the credential (6.3.6), sent on
 * the path and nowhere else , not logged, not printed, not kept.
 */
export const readDepositLink = async (api: ApiClient, token: string): Promise<DepositLinkView> => {
  const answer = await api.request<{ link?: DepositLinkView }>(
    "/api/v1/public/deposit-links/{token}",
    "get",
    {
      params: { token },
    },
  );
  if (answer.link === undefined) throw new Error(t("client.session.inattendue"));
  return answer.link;
};

export const depositByLink = async (
  api: ApiClient,
  fetchImpl: typeof globalThis.fetch,
  token: string,
  file: File,
  declaredMonths?: number,
): Promise<DepositLinkView> => {
  declaredMonthsSchema.optional().parse(declaredMonths);
  const ticket = ticketOf(
    await api.request<{ ticket?: unknown }>("/api/v1/public/deposit-links/{token}/upload-ticket", "post", {
      params: { token },
      body: { fileName: file.name, mimeType: file.type, sizeBytes: file.size },
    }),
  );
  await putTo(fetchImpl, ticket, file);
  const answer = await api.request<{ link?: DepositLinkView }>(
    "/api/v1/public/deposit-links/{token}/confirm",
    "post",
    { params: { token }, body: linkConfirmBodyOf(ticket.id, declaredMonths) },
  );
  if (answer.link === undefined) throw new Error(t("client.session.inattendue"));
  return answer.link;
};
